import { useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import StepShell from '../StepShell';
import SignatureBottomSheet from '../components/SignatureBottomSheet';
import InlinePdfViewer from '../components/InlinePdfViewer';
import { colors } from '../styles';
import HireDocuments from '../../../../../services/HireDocuments';
import { getAgreementSignConfig } from '../utils/buildSteps';
import { buildAgreementPdf } from '../utils/buildAgreementPdf';
import { BaseStepProps } from './BaseStepProps';

interface GenericSignDraft {
  acknowledged: boolean;
}

const blobFromBytes = (bytes: Uint8Array): Blob => new Blob([bytes as any], { type: 'application/pdf' });

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const GenericSignStep = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  draft,
  saveDraft,
  clearDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const agreement = step.agreement;
  const config = agreement ? getAgreementSignConfig(agreement) : null;
  const pdfUrl = step.pdfUrl || agreement?.file;

  const initial = (draft as GenericSignDraft) || { acknowledged: false };
  const [acknowledged, setAcknowledged] = useState(initial.acknowledged);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  const canOpenSignature = acknowledged && !!agreement && !!pdfUrl && !savingDraft;

  const submitWithSignature = async (signaturePng: string) => {
    if (!agreement || !pdfUrl) return;
    const pdfBytes = await buildAgreementPdf({
      pdfUrl,
      config,
      employeeName: tokenData.userName || '',
      signaturePng,
      signatureDate: moment().format('YYYY-MM-DD'),
      documentTitle: agreement.name,
    });
    const pdfSigned = await blobToBase64(blobFromBytes(pdfBytes));

    await HireDocuments('').create({
      type: agreement.key,
      userId: tokenData.userId,
      userName: tokenData.userName,
      userToken: token,
      pdfUrl,
      pdfSigned,
      signature: signaturePng,
      isDraft: false,
      options: { ...config, signPng: signaturePng, signatureDate: moment().format('YYYY-MM-DD') },
    });

    clearDraft();
    toast.success('Document signed.');
    setSignatureOpen(false);
    onNext();
  };

  const handleSaveLater = async () => {
    if (!agreement || !pdfUrl || savingDraft) return;
    setSavingDraft(true);
    try {
      await HireDocuments('').create({
        type: agreement.key,
        userId: tokenData.userId,
        userName: tokenData.userName,
        userToken: token,
        pdfUrl,
        pdfSigned: null,
        signature: '',
        isDraft: true,
        options: { ...config, signPng: '' },
      });
      clearDraft();
      toast.success('Progress saved.');
      onSaveLater();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not save progress.');
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={() => setSignatureOpen(true)}
      nextLabel="Accept & Sign"
      nextDisabled={!canOpenSignature}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{agreement?.name || step.title}</h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6, marginBottom: 12 }}>
        Scroll through the document below, confirm you have read it, then sign at the bottom.
      </p>

      {pdfUrl && <InlinePdfViewer url={pdfUrl} maxHeight="70vh" />}

      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: 14,
          background: '#F9FAFB',
          border: `1px solid ${colors.border}`,
          borderRadius: 10,
          cursor: 'pointer',
          fontSize: 13,
          lineHeight: 1.5,
          marginBottom: 4,
        }}
      >
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => {
            setAcknowledged(e.target.checked);
            saveDraft({ acknowledged: e.target.checked });
          }}
          style={{ marginTop: 2 }}
        />
        <span>
          I hereby acknowledge and agree that my electronic signature on this document is the legal equivalent of my
          handwritten signature, and that I have read and understood the contents of this document.
        </span>
      </label>

      <SignatureBottomSheet
        isOpen={signatureOpen}
        onClose={() => setSignatureOpen(false)}
        onSubmit={submitWithSignature}
        title={`Sign ${agreement?.name || 'Document'}`}
        submitLabel="Accept & Sign"
      />
    </StepShell>
  );
};

export default GenericSignStep;
